// Reusable inline rich-text editor built on TipTap (StarterKit + Link), sharing
// the same toolbar and extension set as the product "Learn More" editor so the
// stored format (a TipTap JSON document string) stays interchangeable.
//
// Controlled-ish: `value` seeds the editor and re-syncs when it changes by
// identity (e.g. a form reset); `onChange` fires with the serialized JSON string
// on every edit. Used by the admin Email page for the confirmation-email
// intro/outro copy.
import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Box, Typography } from '@mui/material';
import { parseTiptapValue, tiptapToJsonString } from '../../utils/rtf-helpers.js';
import LearnMoreEditorToolbar from './product-editor/LearnMoreEditorToolbar.jsx';

const EDITOR_CONTENT_SX = {
  flex: 1,
  overflow: 'auto',
  px: 2,
  py: 1.5,
  minHeight: 160,
  '& .tiptap': {
    minHeight: 140,
    outline: 'none',
    fontSize: '0.9rem',
    lineHeight: 1.6,
    color: 'text.primary',
    '& p': { mt: 0, mb: 1 },
    '& h2': {
      mt: 1.5, mb: 0.5, fontSize: '1.1rem', fontWeight: 700,
    },
    '& h3': {
      mt: 1.5, mb: 0.5, fontSize: '1rem', fontWeight: 600,
    },
    '& ul, & ol': { pl: 3, mb: 1 },
    '& li': { mb: 0.25 },
    '& a': { color: 'primary.main' },
    '& blockquote': {
      borderLeft: '3px solid', borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary',
    },
    '& .ProseMirror-focused': { outline: 'none' },
    '& p.is-editor-empty:first-of-type::before': {
      content: 'attr(data-placeholder)',
      color: 'text.disabled',
      pointerEvents: 'none',
      float: 'left',
      height: 0,
    },
  },
};

const RichTextEditor = ({
  label, value, onChange, placeholder = 'Write here…', ariaLabel,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    content: parseTiptapValue(value),
    onUpdate: ({ editor: ed }) => onChange(tiptapToJsonString(ed.getJSON())),
  });

  // Re-sync when the incoming value changes by identity but differs from the
  // editor's current content — i.e. an external reset, not our own keystroke.
  useEffect(() => {
    if (!editor) return;
    const current = tiptapToJsonString(editor.getJSON());
    if (current !== value) {
      editor.commands.setContent(parseTiptapValue(value) ?? { type: 'doc', content: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    <Box>
      {label && (
        <Typography
          variant="body2"
          component="label"
          sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {editor && <LearnMoreEditorToolbar editor={editor} />}
        {editor && (
          <Box sx={EDITOR_CONTENT_SX} aria-label={ariaLabel || label}>
            <EditorContent editor={editor} placeholder={placeholder} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RichTextEditor;
